/**
 * Demo User Guard
 * ===============
 *
 * Centraliza as restrições aplicadas a usuários marcados como `is_demo = true`
 * no `profiles`. Tudo aqui é estritamente aditivo: para usuários reais
 * (`is_demo = false`), `isDemoUser()` retorna `false` e nenhum helper altera
 * comportamento. Nenhuma mudança em roles, RLS, ou MODULE_*_PERMISSIONS.
 *
 * Surfaces:
 *  - UI: ProtectedRoute usa isDemoBlockedRoute() para barrar rotas estruturais
 *  - UI: componentes de exportação usam canExport() para esconder botões
 *  - Backend (RPC): cada exportar_*_csv checa profiles.is_demo
 *  - Backend (Edge): gerar-relatorio-pdf checa profiles.is_demo
 *
 * Cobertura de rotas:
 *  Como o app aninha áreas administrativas tanto no top-level (`/usuarios`)
 *  quanto sob namespaces (`/empreendimentos/usuarios`, `/financiamento/admin`),
 *  o matcher abaixo bloqueia tokens estruturais em QUALQUER segmento do path,
 *  não apenas como prefixo, evitando bypass via URL aninhada.
 */

export interface DemoUserShape {
  isDemo?: boolean | null;
}

/**
 * Tokens estruturais/administrativos bloqueados para usuários demo.
 * Match = token aparece como segmento completo de qualquer ponto do path.
 * Ex.: 'usuarios' bloqueia /usuarios, /empreendimentos/usuarios, /x/y/usuarios/z
 */
export const DEMO_BLOCKED_SEGMENTS: readonly string[] = Object.freeze([
  'usuarios',
  'configuracoes',
  'settings',
  'clusters',
  'integracao',
  'sandbox',
  'data-cleanup',
  'ferramentas-dev',
  'qa-dashboard',
  'observabilidade',
  'sla-uptime',
  'siafo-import',
  'auditoria',
  'manual',
  'docs',
  'help',
  'wiki',
  'admin',
  'if-admin',
  'rkg-governance',
  'gestor-performance',
]);

/**
 * Rotas full-path (top-level) bloqueadas adicionalmente.
 * Cobrem áreas como /financiamento/admin (segment 'admin' já cobre, mas mantemos
 * explícito para auditabilidade) e a página pública de apresentação interna.
 */
export const DEMO_BLOCKED_ROUTES: readonly string[] = Object.freeze([
  '/financiamento/admin',
  '/apresentacao',
]);

export function isDemoUser(u?: DemoUserShape | null): boolean {
  return !!u?.isDemo;
}

/** True quando exportação é permitida. Default permissivo. */
export function canExport(u?: DemoUserShape | null): boolean {
  return !isDemoUser(u);
}

export function isDemoBlockedRoute(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  const p = pathname.toLowerCase().split('?')[0].split('#')[0];

  // Prefix match em rotas full-path explícitas
  for (const prefix of DEMO_BLOCKED_ROUTES) {
    if (p === prefix || p.startsWith(prefix + '/')) return true;
  }

  // Segment match: bloqueia se algum segmento do path estiver na blocklist
  const segments = p.split('/').filter(Boolean);
  for (const seg of segments) {
    if (DEMO_BLOCKED_SEGMENTS.includes(seg)) return true;
  }

  return false;
}

/* ============================================================================
 * Virtual Frente (UI-only fallback para usuários demo)
 * ----------------------------------------------------------------------------
 * Permite que usuários `is_demo = true` registrem execuções em contratos sem
 * `emp_frentes_trabalho` cadastradas, SEM persistir frente virtual no banco
 * e SEM alterar RBAC/RLS/schema. A coluna `frente_id` em
 * `emp_validacoes_execucao` já é NULLABLE, portanto na submissão o id virtual
 * é mapeado para `null` antes do insert.
 * ========================================================================== */

export interface VirtualFront {
  id: string;
  nome: string;
  isVirtual: true;
}

export const VIRTUAL_FRONT_PREFIX = 'virtual-frente-';

export function deriveVirtualFront(
  contratoId: string,
  localidade?: string | null,
): VirtualFront {
  const suffix = localidade && localidade.trim() ? ` — ${localidade.trim()}` : '';
  return {
    id: `${VIRTUAL_FRONT_PREFIX}${contratoId}`,
    nome: `Frente simulada${suffix}`,
    isVirtual: true,
  };
}

export function isVirtualFrontId(id: string | null | undefined): boolean {
  return !!id && id.startsWith(VIRTUAL_FRONT_PREFIX);
}

/** Normaliza o `frente_id` enviado ao backend: virtual → null. */
export function resolveFrenteIdForInsert(id: string | null | undefined): string | null {
  if (!id) return null;
  return isVirtualFrontId(id) ? null : id;
}
